import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Heart, DollarSign, Calendar, Users, ChevronRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { API } from '../App';

function Contact() {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose'); // 'choose', 'callback', 'register', 'success'
  const [submitting, setSubmitting] = useState(false);
  const [callbackForm, setCallbackForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reason: '',
    preferredTime: 'anytime'
  });
  const [settings, setSettings] = useState({
    phone: "(541) 808-9393",
    email: "info@coosbaycornerstone.com",
    address: "63748 Harriet Road",
    city_state_zip: "Coos Bay, Oregon 97420",
    sunday_school_time: "9:15 AM",
    sunday_service_time: "10:30 AM"
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const formatPhoneForCall = (phone) => {
    return phone.replace(/[^\d+]/g, '');
  };

  const getGoogleMapsUrl = () => {
    const address = encodeURIComponent(`${settings.address}, ${settings.city_state_zip}`);
    return `https://www.google.com/maps/search/?api=1&query=${address}`;
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Submit callback request
      await axios.post(`${API}/callback-request`, callbackForm);
      setStep('success');
      toast.success('Your callback request has been submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Contact options with their actions
  const contactOptions = [
    {
      id: 'prayer',
      icon: Heart,
      title: 'Prayer Request',
      description: 'Submit a prayer request to our prayer team',
      color: '#dc2626',
      action: () => navigate('/prayer')
    },
    {
      id: 'giving',
      icon: DollarSign,
      title: 'Giving & Donations',
      description: 'Support our ministry through your generous giving',
      color: '#16a34a',
      action: () => navigate('/giving')
    },
    {
      id: 'events',
      icon: Calendar,
      title: 'Events & Activities',
      description: 'Find out about upcoming events and activities',
      color: '#2563eb',
      action: () => navigate('/events')
    },
    {
      id: 'visit',
      icon: MapPin,
      title: 'Directions & Service Times',
      description: 'Get directions and learn about our services',
      color: '#f59e0b',
      action: () => window.open(getGoogleMapsUrl(), '_blank')
    },
    {
      id: 'pastoral',
      icon: Users,
      title: 'Connect with Pastoral Staff',
      description: 'Request a personal call back from our pastoral team',
      color: '#7c3aed',
      action: () => setStep('callback')
    }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="section-padding bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="contact-title">
            Contact Us
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            We&apos;d love to hear from you and help in any way we can
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="section-padding bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Step: Choose How We Can Help */}
          {step === 'choose' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
                  How Can We Help You?
                </h2>
                <p className="text-xl text-slate-600">
                  Select an option below and we&apos;ll point you in the right direction
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {contactOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={option.action}
                    className="card p-6 text-left hover:shadow-xl transition-all hover:scale-105 group"
                    data-testid={`contact-option-${option.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="p-3 rounded-xl"
                        style={{ backgroundColor: `${option.color}20` }}
                      >
                        <option.icon size={28} style={{ color: option.color }} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-1" style={{ color: '#1e3a8a' }}>
                          {option.title}
                        </h3>
                        <p className="text-slate-600 text-sm">
                          {option.description}
                        </p>
                      </div>
                      <ChevronRight size={24} className="text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                    </div>
                  </button>
                ))}
              </div>

              {/* Quick Contact Info */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: '#1e3a8a' }}>
                  Quick Contact Information
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <a 
                    href={`tel:${formatPhoneForCall(settings.phone)}`}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-blue-50 transition-colors"
                  >
                    <Phone size={24} className="text-amber-500" />
                    <div>
                      <p className="font-semibold" style={{ color: '#1e3a8a' }}>Call Us</p>
                      <p className="text-slate-600">{settings.phone}</p>
                    </div>
                  </a>
                  
                  <a 
                    href={getGoogleMapsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-blue-50 transition-colors"
                  >
                    <MapPin size={24} className="text-amber-500" />
                    <div>
                      <p className="font-semibold" style={{ color: '#1e3a8a' }}>Visit Us</p>
                      <p className="text-slate-600 text-sm">{settings.address}</p>
                    </div>
                  </a>
                  
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                    <Clock size={24} className="text-amber-500" />
                    <div>
                      <p className="font-semibold" style={{ color: '#1e3a8a' }}>Service Times</p>
                      <p className="text-slate-600 text-sm">Sun: {settings.sunday_school_time} & {settings.sunday_service_time}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step: Callback Request Form */}
          {step === 'callback' && (
            <div className="max-w-2xl mx-auto">
              <button 
                onClick={() => setStep('choose')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 font-semibold"
              >
                <ArrowLeft size={20} />
                Back to options
              </button>

              <div className="card p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users size={32} className="text-purple-600" />
                  </div>
                  <h2 className="text-3xl font-bold mb-2" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
                    Request a Callback
                  </h2>
                  <p className="text-slate-600">
                    Fill out this short form and a member of our pastoral staff will reach out to you personally.
                  </p>
                </div>

                <form onSubmit={handleCallbackSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>First Name *</label>
                      <input
                        type="text"
                        required
                        value={callbackForm.firstName}
                        onChange={(e) => setCallbackForm({ ...callbackForm, firstName: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="John"
                        data-testid="callback-firstname"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Last Name *</label>
                      <input
                        type="text"
                        required
                        value={callbackForm.lastName}
                        onChange={(e) => setCallbackForm({ ...callbackForm, lastName: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Doe"
                        data-testid="callback-lastname"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Email Address *</label>
                    <input
                      type="email"
                      required
                      value={callbackForm.email}
                      onChange={(e) => setCallbackForm({ ...callbackForm, email: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="john@example.com"
                      data-testid="callback-email"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Phone Number *</label>
                    <input
                      type="tel"
                      required
                      value={callbackForm.phone}
                      onChange={(e) => setCallbackForm({ ...callbackForm, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(541) 123-4567"
                      data-testid="callback-phone"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>Best Time to Call</label>
                    <select
                      value={callbackForm.preferredTime}
                      onChange={(e) => setCallbackForm({ ...callbackForm, preferredTime: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="callback-time"
                    >
                      <option value="anytime">Anytime</option>
                      <option value="morning">Morning (9am - 12pm)</option>
                      <option value="afternoon">Afternoon (12pm - 5pm)</option>
                      <option value="evening">Evening (5pm - 8pm)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2" style={{ color: '#1e3a8a' }}>What would you like to discuss? (Optional)</label>
                    <textarea
                      rows={4}
                      value={callbackForm.reason}
                      onChange={(e) => setCallbackForm({ ...callbackForm, reason: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Please share a brief description of what you'd like to talk about..."
                      data-testid="callback-reason"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary text-lg py-4"
                    data-testid="callback-submit"
                  >
                    {submitting ? 'Submitting...' : 'Request Callback'}
                  </button>

                  <p className="text-sm text-slate-500 text-center">
                    Your information is secure and will only be used to contact you.
                  </p>
                </form>
              </div>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="card p-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={48} className="text-green-600" />
                </div>
                <h2 className="text-3xl font-bold mb-4" style={{ color: '#1e3a8a', fontFamily: 'Playfair Display, serif' }}>
                  Request Submitted!
                </h2>
                <p className="text-xl text-slate-600 mb-8">
                  Thank you for reaching out! A member of our pastoral staff will contact you soon.
                </p>
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      setStep('choose');
                      setCallbackForm({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        reason: '',
                        preferredTime: 'anytime'
                      });
                    }}
                    className="btn-primary px-8 py-3"
                  >
                    Back to Contact Options
                  </button>
                  <p className="text-slate-500">
                    Or explore our website:
                  </p>
                  <div className="flex justify-center gap-4 flex-wrap">
                    <button onClick={() => navigate('/')} className="px-4 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                      Home
                    </button>
                    <button onClick={() => navigate('/events')} className="px-4 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                      Events
                    </button>
                    <button onClick={() => navigate('/about')} className="px-4 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                      About Us
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Contact;
